import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import NewFooter from "../../components/Footers/NewFooter";
import NewNavbar from "../../components/Navbar/NewNavbar";
import RichText from "../../components/RichText/RichText";
import Seo from "../../components/Seo/Seo";
import { FREE_MONTHLY_CREDITS } from "../../constants/credits";
import { getResource, resources } from "../../content/resources";
import { clusterName } from "../../content/resources/clusters";
import { articleJsonLd } from "../../content/resources/schema";
import NotFoundPage from "../NotFoundPage/NotFoundPage";

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  padding: 120px 0 32px;
  background: #ffffff;
`;

const Wrap = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Crumbs = styled.nav`
  font-size: 14px;
  color: #9ca3af;
  margin-bottom: 16px;

  a {
    color: #6b7280;
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`;

const Title = styled.h1`
  font-size: 42px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.15;
  margin-bottom: 16px;

  @media (max-width: 767px) {
    font-size: 32px;
  }
`;

const Meta = styled.p`
  font-size: 14px;
  color: #9ca3af;
  margin: 0;
`;

const Article = styled.article`
  background: #f9fafb;
  padding: 32px 0 64px;
  flex: 1;

  font-size: 17px;
  line-height: 1.7;
  color: #2a2a2a;

  h2 {
    font-size: 26px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 40px 0 12px;
    scroll-margin-top: 100px;
  }
  h3 {
    font-size: 21px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 28px 0 10px;
    scroll-margin-top: 100px;
  }
  p {
    margin: 0 0 16px;
  }
  ul,
  ol {
    margin: 0 0 16px;
    padding-left: 24px;
  }
  li {
    margin-bottom: 8px;
  }
  a {
    color: #6b7280;
    font-weight: 600;
  }
`;

const Tldr = styled.div`
  background: #eef2ff;
  border: 1px solid #e0e7ff;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 32px;
  font-size: 17px;
  line-height: 1.6;
  color: #1a1a1a;
`;

const FaqSection = styled.section`
  margin-top: 48px;
  border-top: 1px solid #e5e7eb;
  padding-top: 32px;
`;

const FaqQ = styled.h3`
  scroll-margin-top: 100px;
`;

const Cta = styled.div`
  margin-top: 48px;
  background: #1a1a1a;
  border-radius: 16px;
  padding: 32px;
  text-align: center;
  color: #fff;

  h3 {
    color: #fff;
    margin: 0 0 8px;
    font-size: 22px;
  }
  p {
    color: #d1d5db;
    margin: 0 0 20px;
  }
`;

const CtaButton = styled(Link)`
  display: inline-block;
  background: #fff;
  color: #1a1a1a;
  padding: 14px 32px;
  border-radius: 50px;
  font-weight: 700;
  text-decoration: none;
  &:hover {
    opacity: 0.9;
    text-decoration: none;
  }
`;

const Related = styled.section`
  margin-top: 48px;

  h2 {
    font-size: 22px;
    margin-bottom: 16px;
  }
  ul {
    list-style: none;
    padding: 0;
  }
  li {
    margin-bottom: 10px;
  }
  a {
    color: #6b7280;
    font-weight: 600;
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`;

/** @param {string | undefined} iso */
const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

/** @param {string | import("../../content/richText").Span[] | undefined} tldr */
const renderTldr = (tldr) => {
  if (!tldr) return null;
  if (typeof tldr === "string") {
    return (
      <Tldr>
        <strong>The short answer: </strong>
        {tldr}
      </Tldr>
    );
  }
  return (
    <Tldr>
      <strong>The short answer: </strong>
      <RichText blocks={[{ type: "p", spans: tldr }]} />
    </Tldr>
  );
};

const ResourcePage = () => {
  const { slug } = /** @type {{slug: string}} */ (useParams());
  const article = getResource(slug);

  if (!article) return <NotFoundPage />;

  const related = (article.related || [])
    .map((/** @type {string} */ s) => getResource(s))
    .filter((/** @type {unknown} */ item) => item != null)
    .slice(0, 4);

  // Fall back to a few recent articles if none were specified.
  const relatedList =
    related.length > 0
      ? related
      : resources.filter((/** @type {any} */ a) => a.slug !== article.slug).slice(0, 3);

  return (
    <Page>
      <Seo
        title={article.metaTitle || `${article.title} | Listino`}
        description={article.description}
        path={`/resources/${article.slug}`}
        image={article.image}
        jsonLd={articleJsonLd(article)}
        noindex={!!article.draft}
      />
      <NewNavbar />

      <Header>
        <Wrap>
          <Crumbs>
            <Link to="/">Home</Link> / <Link to="/resources">Resources</Link> /{" "}
            {clusterName(article.cluster)}
          </Crumbs>
          <Title>{article.title}</Title>
          <Meta>
            {article.audience ? `${article.audience} · ` : ""}
            Updated {formatDate(article.dateModified || article.datePublished)}
            {article.readingTime ? ` · ${article.readingTime}` : ""}
          </Meta>
        </Wrap>
      </Header>

      <Article>
        <Wrap>
          {renderTldr(article.tldr)}

          <RichText blocks={article.blocks} />

          {article.faqs && article.faqs.length > 0 && (
            <FaqSection>
              <h2>Frequently asked questions</h2>
              {article.faqs.map((/** @type {any} */ faq) => (
                <div key={faq.question}>
                  <FaqQ
                    id={faq.question
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/^-|-$/g, "")}
                  >
                    {faq.question}
                  </FaqQ>
                  <RichText blocks={faq.answer} />
                </div>
              ))}
            </FaqSection>
          )}

          <Cta>
            <h3>See exactly what to fix in your listing</h3>
            <p>
              Get a Listino listing prep — optimized description, photo strategy, and a Quick CMA.
              Start free — every account includes {FREE_MONTHLY_CREDITS.toLocaleString("en-US")}{" "}
              credits a month.
            </p>
            <CtaButton to={article.primaryCtaPath || "/get-started"}>Get started</CtaButton>
          </Cta>

          {relatedList.length > 0 && (
            <Related>
              <h2>Keep reading</h2>
              <ul>
                {relatedList.map((/** @type {any} */ a) => (
                  <li key={a.slug}>
                    <Link to={`/resources/${a.slug}`}>{a.title}</Link>
                  </li>
                ))}
              </ul>
            </Related>
          )}
        </Wrap>
      </Article>

      <NewFooter />
    </Page>
  );
};

export default ResourcePage;
